import { ReportType } from 'components/pages/financials';
import {
  ActionType,
  FinancialsState,
  LoadingType,
  SetDepositPayload,
  SetFilterPayload,
  SetFiltersPayload,
  SetOrgAdminAccessPayload,
  SetOrganizationPayload,
  SetOutstandingReportPayload,
  SetTransactionsPayload,
} from './types';

export interface FinancialsAction {
  type: ActionType;
  loading?: { loadingType: LoadingType; isLoading: boolean };
  payload?:
    | SetOrgAdminAccessPayload
    | SetOrganizationPayload
    | SetFiltersPayload
    | SetFilterPayload
    | SetDepositPayload
    | SetOutstandingReportPayload
    | SetTransactionsPayload;
}
export enum DepositStatus {
  FAILED = 'failed',
  PAID = 'paid',
  // enum from db schema below not currently in use 11/15/24
  PENDING = 'pending',
  CANCELLED = 'canceled',
  IN_TRNASIT = 'in_transit',
}

/**
 * This function sets the state of the financials context. It performs the given action on the state
 * and returns the updated state.
 * @param state The current state of the financials context
 * @param action The action to perform on the state, with the given payload
 * @returns The updated state of the financials context
 */
export const financialsReducer = (
  state: FinancialsState,
  action: FinancialsAction
): FinancialsState => {
  switch (action.type) {
    case ActionType.SetOrgAdminAccess: {
      const { hasOrgAdminAccess } = action.payload as SetOrgAdminAccessPayload;
      return { ...state, hasOrgAdminAccess };
    }
    case ActionType.SetOrganization: {
      const { organization } = action.payload as SetOrganizationPayload;
      return { ...state, organization };
    }
    case ActionType.UpdateFilter: {
      const { key, value, reportType } = action.payload as SetFilterPayload;
      switch (reportType) {
        case ReportType.Deposits:
          return {
            ...state,
            depositFilters: { ...state.depositFilters, [key]: value },
          };
        case ReportType.Outstanding:
          return {
            ...state,
            outstandingFilters: { ...state.outstandingFilters, [key]: value },
          };
        case ReportType.Transactions:
          return {
            ...state,
            transactionFilters: { ...state.transactionFilters, [key]: value },
          };
        default:
          return state;
      }
    }
    case ActionType.SetLoading: {
      const { loadingType, isLoading } = action.loading;
      return {
        ...state,
        loading: {
          ...state.loading,
          [loadingType]: isLoading,
        },
      };
    }
    case ActionType.SetDeposits: {
      const { deposits } = action.payload as SetDepositPayload;
      return {
        ...state,
        deposits,
        depositSummary: (deposits || []).reduce((acc, d) => {
            if (d.status === DepositStatus.FAILED) return acc;
            return {
            gross: acc.gross + d.grossAmount,
            net: acc.net + d.netAmount,
            fees: acc.fees + d.fees,
          }},
          { gross: 0, net: 0, fees: 0 }
        ),
      };
    }
    case ActionType.SetOutstandingReports: {
      const { outstandingReports } =
        action.payload as SetOutstandingReportPayload;
      return {
        ...state,
        outstandingReports,
        outstandingSummary: (outstandingReports || []).reduce(
          (acc, d) => ({
            overdueInvoices:
              acc.overdueInvoices +
              (d.orderType === 'Invoice' && d.status === 'Past Due' ? 1 : 0),
            unpaidRegistration:
              acc.unpaidRegistration +
              (d.orderType === 'Registration' &&
              (d.status === 'Past Due' || d.status === 'Pending Cash Payment')
                ? 1
                : 0),
            scheduled: acc.scheduled + (d.status === 'Scheduled' ? 1 : 0),
          }),
          { scheduled: 0, unpaidRegistration: 0, overdueInvoices: 0 }
        ),
      };
    }
    case ActionType.SetOutstandingFilters: {
      const { filters } = action.payload as SetFiltersPayload;
      return { ...state, outstandingFilters: filters };
    }
    case ActionType.SetTransactions: {
      const { transactions } = action.payload as SetTransactionsPayload;
      return {
        ...state,
        transactions,
        transactionSummary: transactions.reduce(
          (acc, d) => ({
            gross: acc.gross + d.grossAmount,
            net: acc.net + d.netAmount,
            fees: acc.fees + (d.grossAmount - d.netAmount),
            refunds: acc.refunds + (d.status === 'Refunded' ? 1 : 0),
            failed: acc.failed + (d.status === 'Failed' ? 1 : 0),
          }),
          { gross: 0, net: 0, fees: 0, refunds: 0, failed: 0 }
        ),
      };
    }
    case ActionType.SetTransactionsFilters: {
      const { filters } = action.payload as SetFiltersPayload;
      return { ...state, transactionFilters: filters };
    }
    default:
      throw new Error(`Unhandled action type: ${action.type}`);
  }
};
